/**
 * Jentis Product List Tracking Module
 * 
 * Implements productlist event tracking for all product/tariff lists in the Shopfront.
 * Fires when [data-buy] elements become 50% visible in viewport.
 * 
 * Also handles Jentis SDK initialization and pageview tracking.
 * 
 * Tracking Flow (compliant with acceptance criteria):
 * 1. Initialize Jentis SDK
 * 2. Send initial pageview tracking
 * 3. When ANY product becomes visible, collect ALL products on page
 * 4. For each product: _jts.push({ track: "product", type: "productlist", id, quantity: 1, position })
 * 5. Push list context: _jts.push({ track: "productlist", listname: [pagetype] })
 * 6. Submit data: _jts.push({ track: "submit" })
 * 
 * The entire tracking event fires only once per unique combination of products.
 * 
 * Dependencies:
 * - trackOrderButtonsInView.js (for visibility tracking)
 * - debug-console.js (for debugging output)
 */

import debugConsoleModule from '../debug-console.js';
import { eventOnElementInView } from '../trackOrderButtonsInView.js';

// Create namespaced debug console for this module
const debugConsole = debugConsoleModule.createNamespace('jentis');

// Track if pageview has already been sent to prevent duplicates
let pageviewTracked = false;

/**
 * Initializes the Jentis SDK with configuration
 * @param {Object} config - Jentis configuration object
 */
/* eslint-disable max-lines-per-function, complexity */
function initJentisSDK(config) {
    const { host, id, stage, script } = config;
    
    // Check for preview version in storage
    let version = '_';
    if (
        window.localStorage !== null &&
        typeof window.localStorage === 'object' &&
        typeof window.localStorage.getItem === 'function' &&
        window.sessionStorage !== null &&
        typeof window.sessionStorage === 'object' &&
        typeof window.sessionStorage.getItem === 'function'
    ) {
        version = 
            window.sessionStorage.getItem('jts_preview_version') ||
            window.localStorage.getItem('jts_preview_version') ||
            version;
    }
    
    // Set up Jentis configuration
    window.jentis = window.jentis || {};
    window.jentis.config = window.jentis.config || {};
    window.jentis.config.frontend = window.jentis.config.frontend || {};
    window.jentis.config.frontend.cdnhost = host + '/get/' + stage + '/web/' + version + '/';
    window.jentis.config.frontend.vers = version;
    window.jentis.config.frontend.env = stage;
    window.jentis.config.frontend.project = id;
    
    // Initialize _jts array
    window._jts = window._jts || [];
    
    // Load Jentis script
    const firstScript = document.getElementsByTagName('script')[0];
    const jentisScript = document.createElement('script');
    jentisScript.async = true;
    jentisScript.src = window.jentis.config.frontend.cdnhost + script;
    firstScript.parentNode.insertBefore(jentisScript, firstScript);
}
/* eslint-enable max-lines-per-function, complexity */

/**
 * Sends initial pageview tracking to Jentis
 * @param {Object} pageData - Page tracking data
 */
/* eslint-disable max-lines-per-function, complexity */
function trackPageview(pageData) {
    // Prevent duplicate pageview tracking
    if (pageviewTracked) {
        return;
    }
    
    if (!window._jts || !Array.isArray(window._jts)) {
        return;
    }
    
    const pageviewData = {
        track: 'pageview',
        applicationName: pageData.applicationName || '',
        hashedCustomerId: pageData.hashedCustomerId || '',
        loginStatus: pageData.loginStatus || false,
        page: pageData.page || window.location.pathname,
        productArea: pageData.productArea || '5',
        productSection: pageData.productSection || '',
        subShopname: pageData.subShopname || '',
        countryMarket: pageData.countryMarket || ''
    };
    
    window._jts.push(pageviewData);
    debugConsole.log('_jts.push(pageview):', pageviewData);
    window._jts.push({ track: 'submit' });
    debugConsole.log('_jts.push(submit)');
    
    // Mark pageview as tracked
    pageviewTracked = true;
}
/* eslint-enable max-lines-per-function, complexity */

/**
 * Gets the current page type to use as listname for productlist tracking
 * @returns {string} The page type from the pageview data
 */
/* eslint-disable max-lines-per-function, complexity */
function getPageType() {
    // Try to get pagetype from Jentis pageview data
    if (window._jts && Array.isArray(window._jts)) {
        const pageviewData = window._jts.find(item => item.track === 'pageview');
        if (pageviewData && pageviewData.productArea) {
            return pageviewData.productArea;
        }
    }
    
    // Primary fallback: check dataLayer[0].pagetype (standard STRATO format)
    if (window.dataLayer && Array.isArray(window.dataLayer) && window.dataLayer[0]) {
        if (window.dataLayer[0].pagetype) {
            return window.dataLayer[0].pagetype;
        }
    }
    
    // Secondary fallback: check dataLayer for pageview with productArea
    if (window.dataLayer && Array.isArray(window.dataLayer)) {
        const pageviewData = window.dataLayer.find(item => item.track === 'pageview');
        if (pageviewData && pageviewData.productArea) {
            return pageviewData.productArea;
        }
    }

    // Final fallback
    return 'unknown';
}
/* eslint-enable max-lines-per-function, complexity */

/**
 * Checks if an element is currently visible in the viewport
 * @param {Element} element - The element to check
 * @returns {boolean} True if the element is visible
 */
function isElementVisible(element) {
    const rect = element.getBoundingClientRect();
    return rect.top < window.innerHeight && 
           rect.bottom > 0 && 
           rect.left < window.innerWidth && 
           rect.right > 0;
}

/**
 * Gets unique visible products from all [data-buy] elements, deduplicated by product ID
 * @returns {Array} Array of unique visible product elements
 */
function getUniqueVisibleProducts() {
    const allProductElements = document.querySelectorAll('[data-buy]');
    const visibleElements = Array.from(allProductElements).filter(isElementVisible);
    
    // Deduplicate by product ID (data-buy attribute value)
    const uniqueProducts = [];
    const seenProductIds = new Set();
    
    visibleElements.forEach(element => {
        const productId = element.getAttribute('data-buy');
        if (!seenProductIds.has(productId)) {
            seenProductIds.add(productId);
            uniqueProducts.push(element);
        }
    });
    
    return uniqueProducts;
}

/**
 * Pushes individual product data to _jts array
 * @param {Array} products - Array of product elements
 */
function pushProductData(products) {
    products.forEach((element, index) => {
        const productId = element.getAttribute('data-buy');
        const position = index + 1; // 1-based position indexing
        
        const productData = {
            track: 'product',
            type: 'productlist',
            id: productId,
            quantity: 1,
            position: position
        };
        
        window._jts.push(productData);
        debugConsole.log('_jts.push(product):', productData);
    });
}

/**
 * Pushes productlist context and submit data to _jts array
 */
function pushProductListContext() {
    const listname = getPageType();
    const productlistData = {
        track: 'productlist',
        listname: listname
    };
    
    window._jts.push(productlistData);
    debugConsole.log('_jts.push(productlist):', productlistData);

    const submitData = {
        track: 'submit'
    };
    
    window._jts.push(submitData);
    debugConsole.log('_jts.push(submit):', submitData);
}

/**
 * Tracks all visible products as a productlist event
 */
function trackVisibleProducts() {
    if (!window._jts || !Array.isArray(window._jts)) {
        return;
    }

    // Get unique visible products (deduplicated by product ID)
    const products = getUniqueVisibleProducts();
    
    if (products.length === 0) {
        return;
    }

    // 1. Push individual product tracking for each visible product
    pushProductData(products);

    // 2. Push the productlist context and submit data
    pushProductListContext();
}

// Track which product combinations have been processed to prevent duplicates
const trackedProductLists = new Set();

/**
 * Creates a unique key for the current combination of visible products
 * @returns {string} Comma-separated list of visible product IDs
 */
function getVisibleProductsKey() {
    const products = getUniqueVisibleProducts();
    
    return products
        .map(el => el.getAttribute('data-buy'))
        .sort()
        .join(',');
}

/**
 * Handles the productInView event by tracking visible products
 */
function handleProductInView() {
    // Create a unique key for this combination of visible products
    const visibleProductIds = getVisibleProductsKey();

    // Only track if this combination hasn't been tracked before
    if (!trackedProductLists.has(visibleProductIds)) {
        trackedProductLists.add(visibleProductIds);
        trackVisibleProducts();
    }
}

/**
 * Handles add to cart tracking when a product button is clicked
 * @param {Event} event - The mousedown event
 */
/* eslint-disable max-lines-per-function */
function handleAddToCart(event) {
    // Check if this event has already been handled by a nested element
    if (event.jentisHandled) {
        return;
    }
    
    // Mark event as handled to prevent parent elements from processing it
    event.jentisHandled = true;
    
    const element = event.currentTarget;
    const productId = element.getAttribute('data-buy');
    
    if (!productId) {
        return;
    }
    
    if (!window._jts || !Array.isArray(window._jts)) {
        return;
    }
    
    // Create add to cart event data
    const addToCartData = {
        track: 'product',
        type: 'addtocart',
        id: productId,
        quantity: 1
    };
    
    window._jts.push(addToCartData);
    debugConsole.log('_jts.push(product addtocart):', addToCartData);
    
    // Push the addtocart tracking command (required by Jentis spec)
    window._jts.push({ track: 'addtocart' });
    debugConsole.log('_jts.push(addtocart)');
    
    window._jts.push({ track: 'submit' });
    debugConsole.log('_jts.push(submit)');
}
/* eslint-enable max-lines-per-function */

/**
 * Initializes add to cart tracking for all [data-buy] elements
 */
function initAddToCartTracking() {
    // Find all elements with data-buy attribute
    const buyButtons = document.querySelectorAll('[data-buy]');
    
    buyButtons.forEach(button => {
        // Add mousedown event listener for add to cart tracking
        button.addEventListener('click', handleAddToCart);
    });
}

/**
 * Initializes the Jentis product list tracking.
 * Observes [data-buy] elements and tracks them when they come into view.
 */
export function initJentisProductListTracking() {
    // Use the existing eventOnElementInView to detect when products come into view
    eventOnElementInView('[data-buy]', 'data-buy', 'productInView', 0.5, document.body);

    // Listen for the custom event and trigger batch tracking
    window.addEventListener('productInView', handleProductInView);
    
    // Initialize add to cart tracking
    initAddToCartTracking();
}

/**
 * Initializes Jentis tracking with SDK setup and pageview tracking
 * @param {Object} config - Configuration object containing Jentis and page data
 */
export function initJentisTracking(config) {
    const { jentis, pageData } = config;
    
    // Initialize Jentis SDK
    if (jentis) {
        initJentisSDK(jentis);
        
        // Track initial pageview after SDK is set up
        if (pageData) {
            // Small delay to ensure SDK is ready
            setTimeout(() => {
                trackPageview(pageData);
            }, 100);
        }
    }
    
    // Initialize product list tracking
    initJentisProductListTracking();
}

// Auto-initialize if window.jentisConfig is available (backward compatibility)
if (window.jentisConfig) {
    initJentisTracking(window.jentisConfig);
} else {
    // Initialize only product list tracking if no global config
    initJentisProductListTracking();
}